import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/data/site";
import Logo from "@/components/Logo";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);
  const isActive = (href) => location.pathname === href;

  return (
    <header
      data-testid="site-nav"
      style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 50,
        background: scrolled ? "rgba(26,22,18,0.92)" : "transparent",
        backdropFilter: scrolled ? "blur(12px)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(12px)" : "none",
        height: scrolled ? 68 : 90,
        borderBottom: scrolled ? "1px solid var(--line)" : "1px solid transparent",
        transition: "background 220ms ease, border-color 220ms ease, backdrop-filter 220ms ease",
      }}
    >
      <div className="site-container" style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Link to="/" data-testid="nav-logo" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
          <Logo height={scrolled ? 42 : 52} />
        </Link>

        <nav className="desk-only" style={{ alignItems: "center", gap: 36, display: "flex" }}>
          {NAV_LINKS.map((l) => (
            <Link
              key={l.label} to={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              style={{
                color: isActive(l.href) ? "var(--gold)" : "var(--text-mid)",
                fontFamily: "Inter, sans-serif",
                fontWeight: 500, fontSize: 15, textDecoration: "none",
                transition: "color 180ms ease",
                position: "relative",
                paddingBottom: 4,
                borderBottom: isActive(l.href) ? "1px solid var(--gold)" : "1px solid transparent",
              }}
              onMouseEnter={(e) => { if (!isActive(l.href)) e.currentTarget.style.color = "var(--text)"; }}
              onMouseLeave={(e) => { if (!isActive(l.href)) e.currentTarget.style.color = "var(--text-mid)"; }}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <Link to="/kapcsolat" data-testid="nav-cta-desktop" className="btn btn-gold desk-only">
          <span>Árajánlatkérés</span>
        </Link>

        <div className="mob-only" style={{ alignItems: "center", gap: 8, display: "flex" }}>
          <a
            href={COMPANY.phoneHref} data-testid="nav-phone-mobile" aria-label="Hívás"
            style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: 44, height: 44, background: "var(--gold)", color: "var(--ink)",
            }}
          >
            <Phone size={17} />
          </a>
          <button
            data-testid="nav-mobile-toggle" aria-label="Menü"
            onClick={() => setOpen(true)}
            style={{
              width: 44, height: 44, background: "transparent",
              border: "1px solid var(--line)", color: "var(--text)",
              display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
            }}
          >
            <Menu size={17} />
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-drawer"
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 60 }}
          onClick={close}
        >
          <aside
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "absolute", top: 0, right: 0, bottom: 0,
              width: "min(340px, 88vw)", background: "var(--ink-2)",
              borderLeft: "1px solid var(--line)", padding: 24,
              display: "flex", flexDirection: "column", gap: 24,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Logo height={40} />
              <button
                data-testid="mobile-drawer-close" onClick={close} aria-label="Bezár"
                style={{ background: "transparent", border: "1px solid var(--line)", color: "var(--text)", width: 36, height: 36, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
              >
                <X size={16} />
              </button>
            </div>
            <nav style={{ display: "flex", flexDirection: "column" }}>
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.label} to={l.href} onClick={close}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  style={{
                    padding: "16px 0", color: isActive(l.href) ? "var(--gold)" : "var(--text)",
                    borderBottom: "1px solid var(--line)", textDecoration: "none",
                    fontWeight: 500, fontSize: 16,
                  }}
                >
                  {l.label}
                </Link>
              ))}
            </nav>
            <Link to="/kapcsolat" onClick={close} data-testid="mobile-cta" className="btn btn-gold" style={{ width: "100%" }}>
              <span>Árajánlatkérés</span>
            </Link>
            <a href={COMPANY.phoneHref} data-testid="mobile-phone" className="btn btn-ghost" style={{ width: "100%" }}>
              {COMPANY.phoneDisplay}
            </a>
          </aside>
        </div>
      )}
    </header>
  );
}
